import React, { useState, useEffect, useCallback } from "react";
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight,
  Link,
  Eye,
  EyeOff,
  MoreHorizontal,
  Globe
} from "lucide-react";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { useAppSelector, useAppDispatch } from "../../redux/store";
import {
  fetchMenuLinks,
  fetchMenuLinkById,
  createMenuLink,
  updateMenuLink,
  deleteMenuLink,
  clearMenuLinksError,
  clearCurrentMenuLink,
  setMenuLinksSearchQuery,
  clearAllMenuLinks,
  setMenuLinksCurrentPage
} from "../../redux/Slices/MenuLinkSlice";
import {
  MenuLink,
  MenuLinkCreateDto,
  MenuLinkUpdateDto,
  MenuLinkVisibilityRef,
  MenuLinkLocationRef
} from "../../modules/shared/model/menu-link.interfaces";
import {
  MenuLinkVisibilityEnum,
  MenuLinkLocationEnum
} from "../../modules/shared/model/menu-link.enum";
import MenuLinkModal from "./MenuLinkModal";
import MenuLinkTranslationDialog from "./MenuLinkTranslationDialog";

const MenuLinkManagement = () => {
  const dispatch = useAppDispatch();
  
  // Redux state
  const {
    menuLinks,
    currentMenuLink,
    menuLinksLoading,
    menuLinksError,
    menuLinksTotalCount,
    menuLinksCurrentPage,
    menuLinksHasNextPage,
    menuLinksHasPreviousPage,
    menuLinksSearchQuery
  } = useAppSelector((state) => state.menuLinks);

  // Local state
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState<MenuLink | null>(null);
  const [translationMenuItem, setTranslationMenuItem] = useState<MenuLink | null>(null);
  const [showTranslationDialog, setShowTranslationDialog] = useState(false);
  const [filterVisibility, setFilterVisibility] = useState<string>("all");
  const [filterLocation, setFilterLocation] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("order");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [pageSize, setPageSize] = useState<number>(10);

  // Load menu links on component mount
  useEffect(() => {
    loadMenuLinks();
  }, []);

  // Load menu links when filters or search change
  useEffect(() => {
    loadMenuLinks();
  }, [menuLinksSearchQuery, filterVisibility, filterLocation, sortBy, sortOrder, pageSize, menuLinksCurrentPage]);

  const loadMenuLinks = useCallback(() => {
    const query: Record<string, string | number | boolean> = {
      page: menuLinksCurrentPage,
      limit: pageSize,
      sort: JSON.stringify([{ orderBy: sortBy, order: sortOrder }])
    };

    if (filterVisibility !== "all") {
      query.filters = JSON.stringify({ visibility: { id: parseInt(filterVisibility) } });
    }

    if (filterLocation !== "all") {
      const existingFilters = query.filters ? JSON.parse(query.filters as string) : {};
      query.filters = JSON.stringify({ 
        ...existingFilters, 
        location: { id: parseInt(filterLocation) } 
      });
    }

    dispatch(fetchMenuLinks(query));
  }, [dispatch, menuLinksCurrentPage, pageSize, filterVisibility, filterLocation, sortBy, sortOrder]);

  const handleSearch = useCallback((query: string) => {
    dispatch(setMenuLinksSearchQuery(query));
  }, [dispatch]);

  const handleCreate = useCallback(async (data: MenuLinkCreateDto) => {
    try {
      await dispatch(createMenuLink(data)).unwrap();
      setShowAddModal(false);
      loadMenuLinks(); // Refresh the list
    } catch (error) {
      console.error("Failed to create menu link:", error);
    }
  }, [dispatch, loadMenuLinks]);

  const handleUpdate = useCallback(async (id: string, data: MenuLinkUpdateDto) => {
    try {
      await dispatch(updateMenuLink({ id, data })).unwrap();
      setSelectedMenuItem(null);
      loadMenuLinks(); // Refresh the list
    } catch (error) {
      console.error("Failed to update menu link:", error);
    }
  }, [dispatch, loadMenuLinks]);

  const handleDelete = useCallback(async (id: string) => {
    if (window.confirm("Are you sure you want to delete this menu link?")) {
      try {
        await dispatch(deleteMenuLink(id)).unwrap();
        loadMenuLinks(); // Refresh the list
      } catch (error) {
        console.error("Failed to delete menu link:", error);
      }
    }
  }, [dispatch, loadMenuLinks]);

  const handleEdit = useCallback((item: MenuLink) => {
    setSelectedMenuItem(item);
  }, []);

  const handleOpenTranslations = useCallback((item: MenuLink) => {
    setTranslationMenuItem(item);
    setShowTranslationDialog(true);
  }, []);

  const handleSaveTranslations = useCallback(async (translations: Record<string, string>) => {
    if (!translationMenuItem) return;
    
    try {
      // Build payload with proper type conversions
      const payload: MenuLinkUpdateDto = {
        title: translationMenuItem.title,
        url: translationMenuItem.url,
        order: translationMenuItem.order,
        translatedTitle: translations,
        visibility: translationMenuItem.visibility ? {
          id: Number(translationMenuItem.visibility.id)
        } : undefined,
        location: translationMenuItem.location ? {
          id: Number(translationMenuItem.location.id)
        } : undefined,
      };

      await dispatch(updateMenuLink({
        id: translationMenuItem.id,
        data: payload
      })).unwrap();
      loadMenuLinks(); // Refresh the list
    } catch (error) {
      console.error("Failed to save translations:", error);
      throw error;
    }
  }, [dispatch, translationMenuItem, loadMenuLinks]);

  const handleRefresh = useCallback(() => {
    loadMenuLinks();
  }, [loadMenuLinks]);

  const handlePageChange = useCallback((newPage: number) => {
    dispatch(setMenuLinksCurrentPage(newPage));
  }, [dispatch]);

  const getVisibilityLabel = (visibilityId: number) => {
    return Object.keys(MenuLinkVisibilityEnum).find(key => MenuLinkVisibilityEnum[key as keyof typeof MenuLinkVisibilityEnum] === visibilityId) || "Unknown";
  };

  const getLocationLabel = (locationId: number) => {
    return Object.keys(MenuLinkLocationEnum).find(key => MenuLinkLocationEnum[key as keyof typeof MenuLinkLocationEnum] === locationId) || "Unknown";
  };

  const getVisibilityColor = (visibilityId: number) => {
    switch (visibilityId) {
      case MenuLinkVisibilityEnum.All: return "bg-green-100 text-green-800";
      case MenuLinkVisibilityEnum.Guest: return "bg-blue-100 text-blue-800";
      case MenuLinkVisibilityEnum.Registered: return "bg-purple-100 text-purple-800";
      case MenuLinkVisibilityEnum.Professional: return "bg-orange-100 text-orange-800";
      case MenuLinkVisibilityEnum.Customer: return "bg-pink-100 text-pink-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const getLocationColor = (locationId: number) => {
    switch (locationId) {
      case MenuLinkLocationEnum.Header: return "bg-blue-100 text-blue-800";
      case MenuLinkLocationEnum.Footer: return "bg-gray-100 text-gray-800";
      case MenuLinkLocationEnum.Sidebar: return "bg-green-100 text-green-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const totalPages = Math.ceil((menuLinksTotalCount || 0) / pageSize);

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-lg sm:text-xl font-semibold">Menu & Footer Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage navigation links and footer items ({menuLinksTotalCount || 0} total)
          </p>
        </div>
        <Button onClick={() => setShowAddModal(true)} className="w-full sm:w-auto text-sm">
          <Plus className="h-4 w-4 mr-2" />
          Add Menu Item
        </Button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search menu items..."
              value={menuLinksSearchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <select
            value={filterVisibility}
            onChange={(e) => setFilterVisibility(e.target.value)}
            className="w-32 h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <option value="all">All Visibility</option>
            <option value={MenuLinkVisibilityEnum.All.toString()}>All Users</option>
            <option value={MenuLinkVisibilityEnum.Guest.toString()}>Guest</option>
            <option value={MenuLinkVisibilityEnum.Registered.toString()}>Registered</option>
            <option value={MenuLinkVisibilityEnum.Professional.toString()}>Professional</option>
            <option value={MenuLinkVisibilityEnum.Customer.toString()}>Customer</option>
          </select>
          <select
            value={filterLocation}
            onChange={(e) => setFilterLocation(e.target.value)}
            className="w-32 h-10 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <option value="all">All Locations</option>
            <option value={MenuLinkLocationEnum.Header.toString()}>Header</option>
            <option value={MenuLinkLocationEnum.Footer.toString()}>Footer</option>
            <option value={MenuLinkLocationEnum.Sidebar.toString()}>Sidebar</option>
          </select>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={menuLinksLoading}>
            <RefreshCw className={`h-4 w-4 ${menuLinksLoading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      {/* Error Display */}
      {menuLinksError && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{menuLinksError}</div>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => dispatch(clearMenuLinksError())}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Menu Items List */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {menuLinksLoading ? (
          <div className="flex justify-center items-center py-8">
            <RefreshCw className="h-6 w-6 animate-spin" />
            <span className="ml-2">Loading menu items...</span>
          </div>
        ) : menuLinks.length === 0 ? (
          <Card className="p-8 text-center">
            <Link className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No menu items found</h3>
            <p className="text-gray-500 mb-4">
              {menuLinksSearchQuery || filterVisibility !== "all" || filterLocation !== "all"
                ? "Try adjusting your search or filters"
                : "Get started by creating your first menu item"
              }
            </p>
            {(!menuLinksSearchQuery && filterVisibility === "all" && filterLocation === "all") && (
              <Button onClick={() => setShowAddModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Menu Item
              </Button>
            )}
          </Card>
        ) : (
          menuLinks.map((item) => (
            <Card key={item.id} className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start">
                <div className="mb-4 sm:mb-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Link className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                    <h3 className="text-base sm:text-lg font-semibold">{item.title}</h3>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Badge 
                      variant="outline" 
                      className={`text-xs sm:text-sm capitalize ${getLocationColor(Number(item.location?.id) || 0)}`}
                    >
                      {getLocationLabel(Number(item.location?.id) || 0)}
                    </Badge>
                    <Badge 
                      variant="outline" 
                      className={`text-xs sm:text-sm capitalize ${getVisibilityColor(Number(item.visibility?.id) || 0)}`}
                    >
                      {getVisibilityLabel(Number(item.visibility?.id) || 0)}
                    </Badge>
                    {item.translatedTitle && Object.keys(item.translatedTitle).length > 0 && (
                      <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 text-xs">
                        🌐 {Object.keys(item.translatedTitle).length}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleOpenTranslations(item)}
                    title="Manage translations"
                  >
                    <Globe className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => handleEdit(item)}
                    title="Edit menu item"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(item.id)}
                    title="Delete menu item"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium mb-2 text-sm sm:text-base">URL</h4>
                  <div className="text-xs sm:text-sm text-gray-600 break-all">{item.url}</div>
                </div>
                <div>
                  <h4 className="font-medium mb-2 text-sm sm:text-base">Display Order</h4>
                  <div className="text-xs sm:text-sm text-gray-600">{item.order}</div>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 bg-white px-4 py-3 sm:px-6">
          <div className="flex flex-1 justify-between sm:hidden">
            <Button
              variant="outline"
              onClick={() => handlePageChange(menuLinksCurrentPage - 1)}
              disabled={!menuLinksHasPreviousPage || menuLinksLoading}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => handlePageChange(menuLinksCurrentPage + 1)}
              disabled={!menuLinksHasNextPage || menuLinksLoading}
            >
              Next
            </Button>
          </div>
          <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {((menuLinksCurrentPage - 1) * pageSize) + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(menuLinksCurrentPage * pageSize, menuLinksTotalCount || 0)}
                </span>{" "}
                of{" "}
                <span className="font-medium">{menuLinksTotalCount || 0}</span>{" "}
                results
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(menuLinksCurrentPage - 1)}
                disabled={!menuLinksHasPreviousPage || menuLinksLoading}
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <Button
                      key={pageNum}
                      variant={pageNum === menuLinksCurrentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => handlePageChange(pageNum)}
                      disabled={menuLinksLoading}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                {totalPages > 5 && (
                  <>
                    <span className="text-gray-500">...</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(totalPages)}
                      disabled={menuLinksLoading}
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(menuLinksCurrentPage + 1)}
                disabled={!menuLinksHasNextPage || menuLinksLoading}
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showAddModal && (
        <MenuLinkModal
          item={null}
          isOpen={true}
          onClose={() => setShowAddModal(false)}
          onSave={handleCreate}
          title="Add Menu Item"
        />
      )}
      
      {selectedMenuItem && (
        <MenuLinkModal
          item={selectedMenuItem}
          isOpen={true}
          onClose={() => setSelectedMenuItem(null)}
          onSave={(data) => handleUpdate(selectedMenuItem.id, data)}
          title="Edit Menu Item"
        />
      )}

      {/* Translation Dialog */}
      <MenuLinkTranslationDialog
        isOpen={showTranslationDialog}
        onClose={() => {
          setShowTranslationDialog(false);
          setTranslationMenuItem(null);
        }}
        menuLink={translationMenuItem}
        onSave={handleSaveTranslations}
      />
    </div>
  );
};

export default MenuLinkManagement;
